import type { ApolloCache, TypedDocumentNode } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';

import { SEEK_ENV } from 'src/config';
import { GetIdDocument } from 'src/types/graphql';

export const getDataFromCache = <Query, T extends Record<string, any>>({
  cache,
  query,
  variables,
}: {
  cache: ApolloCache<T>;
  query: TypedDocumentNode<Query, VariablesOf<Query>>;
  variables?: VariablesOf<TypedDocumentNode<Query>>;
}): Query | undefined => {
  // cache.diff is exposed publicly, but there aren't any docs for it yet.
  // cache.diff will throw if there are any missing fields and returnPartialData
  // is false so we have to handle the errors ourselves if we don't want them to affect prod
  // https://github.com/apollographql/apollo-client/blob/212b1e686359a3489b48d7e5d38a256312f81fde/src/cache/inmemory/readFromStore.ts#L260
  const { result, missing } = cache.diff<Query>({
    query,
    variables,
    optimistic: false,
    returnPartialData: true,
  });
  const isNotProduction = SEEK_ENV !== 'prod';

  if (isNotProduction && missing && missing.length > 0) {
    // eslint-disable-next-line no-console
    missing.forEach((error) => console.log(error));
    throw missing;
  } else {
    return result;
  }
};

export const updateCache = <T>({
  cache,
  data,
  fieldName,
}: {
  cache: ApolloCache<any>;
  data: T;
  fieldName: string;
}) => {
  const result = getDataFromCache({
    cache,
    query: GetIdDocument,
  });

  const _id = result?.viewer?._id;

  cache.modify({
    id: cache.identify({
      __typename: 'Candidate',
      _id,
    }),
    // @ts-expect-error
    fields: {
      [fieldName]: () => data,
    },
  });
};
